import { useEffect, useState } from 'react';
import { Button, Card, Form, Row, Col } from 'react-bootstrap';
import { FaCalendarAlt, FaClock } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import 'react-datepicker/dist/react-datepicker.css';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Props {
  value: {
    date: string;
    timeSlot: string;
  };
  onNext: (data: { date: string; timeSlot: string }) => void;
  onBack: () => void;
}

interface Appointment {
  _id: string;
  date: string;
  timeSlot: string;
}

const timeZone = 'Asia/Dubai';

const generateTimeSlots = (date: Date) => {
  const zonedDate = toZonedTime(date, timeZone);
  const isSunday = zonedDate.getDay() === 0;
  const slots: string[] = [];

  const start = new Date(zonedDate);
  const end = new Date(zonedDate);

  if (isSunday) {
    start.setHours(10, 15, 0, 0); // 10:15 AM Dubai time
    end.setHours(12, 45, 0, 0); // 12:45 PM Dubai time
  } else {
    start.setHours(10, 45, 0, 0); // 10:45 AM Dubai time
    end.setHours(19, 45, 0, 0); // 7:45 PM Dubai time
  }

  while (start <= end) {
    const slotStart = new Date(start);
    start.setMinutes(start.getMinutes() + 30);
    const slotEnd = new Date(start);
    if (slotEnd > end) break;

    const dubaiStart = toZonedTime(slotStart, timeZone);
    const dubaiEnd = toZonedTime(slotEnd, timeZone);

    slots.push(
      `${format(dubaiStart, 'hh:mm a')} – ${format(dubaiEnd, 'hh:mm a')}`
    );

    start.setMinutes(start.getMinutes() - 15); // overlap
  }

  return slots;
};

const DateTimeSelector = ({ value, onNext, onBack }: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value.date ? new Date(value.date) : null
  );
  const [timeSlot, setTimeSlot] = useState(value.timeSlot);
  const [slots, setSlots] = useState<string[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);

  useEffect(() => {
    if (selectedDate) {
      const newSlots = generateTimeSlots(selectedDate);
      setSlots(newSlots);
      setTimeSlot(''); // Reset selected time slot
      setBookedSlots([]); // Clear booked slots for new date

      // Fetch booked appointments for the selected date
      const fetchBookedAppointments = async () => {
        try {
          const formattedDate = format(selectedDate, 'yyyy-MM-dd');
          const response = await fetch(
            `${API_BASE_URL}/sendappointment?date=${formattedDate}`
          );
          if (!response.ok) {
            throw new Error('Failed to fetch appointments');
          }
          const appointments: Appointment[] = await response.json();
          const bookedTimeSlots = appointments
            .filter((appointment) => appointment.date === formattedDate)
            .map((appointment) => appointment.timeSlot);
          setBookedSlots(bookedTimeSlots);
        } catch (error) {
          console.error('Error fetching booked appointments:', error);
          setBookedSlots([]); // Clear booked slots on error
        }
      };

      fetchBookedAppointments();
    } else {
      setSlots([]);
      setBookedSlots([]);
      setTimeSlot('');
    }
  }, [selectedDate]);

  const handleNext = () => {
    if (selectedDate && timeSlot && !bookedSlots.includes(timeSlot)) {
      onNext({
        date: format(selectedDate, 'yyyy-MM-dd'),
        timeSlot,
      });
    }
  };

  return (
    <div >
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <Card className="appointment-border rounded-0">
            <Card.Header className="bg-blue text-light rounded-0 py-4 border-0">
              <p className="mb-0 fw-light letter-spacing-1 fw-semibold text-center ">
                <span className="responsive-text">Select Date & Time</span>
              </p>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} className="mb-5">
                  <Form.Group className="d-flex flex-column align-items-center">
                    {' '}
                    {/* Added flex classes */}
                    <Form.Label className="d-block text-uppercase small fw-bold text-muted mb-3">
                      Select Date
                    </Form.Label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date: Date | null) => setSelectedDate(date)}
                      minDate={new Date()}
                      maxDate={
                        new Date(
                          new Date().getFullYear(),
                          new Date().getMonth() + 2,
                          new Date().getDate()
                        )
                      }
                      inline
                      calendarClassName="border-0 shadow-sm rounded-0 custom-datepicker-calendar" // Added custom class
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-5">
                  <Form.Group>
                    <Form.Label className="d-block text-center text-uppercase small fw-bold text-muted mb-3">
                      Select Time Slot (UAE)
                    </Form.Label>
                    {slots.length ? (
                      <div
                        className="border rounded-0 p-3 bg-light"
                        style={{ maxHeight: '285px', overflowY: 'auto' }}
                      >
                        <Row className="g-2">
                          {slots.map((slot, index) => {
                            const isBooked = bookedSlots.includes(slot);
                            return (
                              <Col key={index} xs={6} sm={4}>
                                <div
                                  className={`p-2 text-center border rounded-0 ${
                                    timeSlot === slot && !isBooked
                                      ? 'bg-red text-light'
                                      : isBooked
                                        ? 'bg-secondary text-white'
                                        : 'border-dark'
                                  }`}
                                  style={{
                                    cursor: isBooked
                                      ? 'not-allowed'
                                      : 'pointer',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.85rem',
                                    opacity: isBooked ? 0.6 : 1,
                                  }}
                                  onClick={() => !isBooked && setTimeSlot(slot)}
                                >
                                  {isBooked ? 'Booked' : slot}
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    ) : (
                      <p className="text-muted mt-2">
                        Select a date to view time slots.
                      </p>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex flex-column flex-md-row justify-content-end mt-4 mt-md-5 pt-3 border-top border-dark">
                <Button
                  onClick={onBack}
                  className="rounded-2 fw-semibold letter-spacing-1 btn-outline-dark mb-2 mb-md-0 me-md-3"
                  style={{
                    minWidth: '120px',
                    minHeight: '45px',
                    outline: 'none',
                    boxShadow: 'none',
                    border: '1px solid #082341',
                    background: 'transparent',
                    color: '#082341',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.outline = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="rounded-2 fw-semibold letter-spacing-1 fw-light btn-dark"
                  style={{
                    minWidth: '160px',
                    minHeight: '45px',
                    outline: 'none',
                    boxShadow: 'none',
                    border: 'none',
                    background: '#082341',
                    color: '#ffffff',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#082341';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.outline = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Next
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DateTimeSelector;
